// Stockage du modèle de lecture local (voir db.js, version 9).
import { db, syncQueue } from '../db';
import { copie } from './paths';

// ---------------------------------------------------------------------------
// Entités
// ---------------------------------------------------------------------------

const estObjet = (valeur) => valeur !== null && typeof valeur === 'object' && !Array.isArray(valeur);

export const idDe = (item) => {
  // Les documents portent `_id` (et non `id`) dans les réponses du serveur.
  const id = item?.id ?? item?._id;
  return typeof id === 'string' || typeof id === 'number' ? String(id) : null;
};

export async function lireEntite(kind, id) {
  const ligne = await db.records.get([kind, String(id)]);
  return ligne && !ligne.supprime ? copie(ligne.data) : null;
}

export async function lireLigne(kind, id) {
  return (await db.records.get([kind, String(id)])) || null;
}

export async function lireEntites(kind) {
  const lignes = await db.records.where('kind').equals(kind).toArray();
  return lignes.filter((ligne) => !ligne.supprime).map((ligne) => copie(ligne.data));
}

export async function lireLignes(kind) {
  return db.records.where('kind').equals(kind).toArray();
}

/**
 * Enregistre des entités reçues du serveur.
 * Une entité modifiée localement et pas encore envoyée n'est jamais écrasée:
 * c'est l'écriture locale, rejouée plus tard, qui doit gagner.
 */
export async function ecrireEntitesServeur(kind, items, { fusion = true, reponseDemandeeLe = null } = {}) {
  const valides = (items || []).filter((item) => estObjet(item) && idDe(item));
  if (valides.length === 0) return 0;
  const maintenant = Date.now();
  await db.transaction('rw', db.records, async () => {
    const existantes = await db.records.bulkGet(valides.map((item) => [kind, idDe(item)]));
    const lignes = [];
    valides.forEach((item, index) => {
      const ancienne = existantes[index];
      if (ancienne?.pending) return;
      // Une réponse demandée AVANT la dernière écriture de cette ligne (saisie
      // locale déjà envoyée et confirmée, mise à jour reçue depuis) est plus
      // ancienne que ce qu'on a: elle ne revient jamais en arrière.
      if (reponseDemandeeLe !== null && ancienne && !ancienne.supprime && (ancienne.ts || 0) > reponseDemandeeLe) return;
      const data = fusion && ancienne ? { ...ancienne.data, ...item } : item;
      lignes.push({
        kind,
        id: idDe(item),
        data,
        ts: maintenant,
        pending: false,
        ...(ancienne?.detailLe ? { detailLe: ancienne.detailLe } : {}),
      });
    });
    if (lignes.length) await db.records.bulkPut(lignes);
  });
  return valides.length;
}

/** Écriture locale: l'entité reste marquée comme modifiée ici jusqu'à la confirmation du serveur. */
export async function ecrireEntiteLocale(kind, id, data) {
  const ancienne = await db.records.get([kind, String(id)]);
  const ligne = { kind, id: String(id), data: copie(data), ts: Date.now(), pending: true };
  if (ancienne?.detailLe) ligne.detailLe = ancienne.detailLe;
  await db.records.put(ligne);
}

/** Le détail complet de cette entité a été reçu du serveur (et non seulement sa ligne de liste). */
export async function marquerDetailRecu(kind, id, instant = Date.now()) {
  if (!id) return;
  await db.records.update([kind, String(id)], { detailLe: instant });
}

export async function marquerConfirmee(kind, id, data) {
  const ligne = await db.records.get([kind, String(id)]);
  await db.records.put({
    kind,
    id: String(id),
    data: data ? copie(data) : ligne?.data || {},
    ts: Date.now(),
    pending: false,
    ...(ligne?.detailLe ? { detailLe: ligne.detailLe } : {}),
  });
}

/** Suppression locale: la ligne reste, masquée, jusqu'à ce que le serveur confirme. */
export async function retirerEntiteLocale(kind, id) {
  const ligne = await db.records.get([kind, String(id)]);
  await db.records.put({
    kind,
    id: String(id),
    data: ligne?.data || { id: String(id) },
    ts: Date.now(),
    pending: true,
    supprime: true,
  });
}

export async function supprimerEntite(kind, id) {
  await db.records.delete([kind, String(id)]);
}

export async function supprimerEntitesServeur(kind, ids) {
  if (!ids?.length) return;
  await db.transaction('rw', db.records, async () => {
    const cles = ids.map((id) => [kind, String(id)]);
    const lignes = await db.records.bulkGet(cles);
    const aSupprimer = cles.filter((cle, index) => lignes[index] && !lignes[index].pending);
    if (aSupprimer.length) await db.records.bulkDelete(aSupprimer);
  });
}

/** Retire les entités d'un type absentes de `idsConnus` (sauf écritures locales). */
export async function purgerAbsentes(kind, idsConnus) {
  const connus = new Set((idsConnus || []).map(String));
  const lignes = await db.records.where('kind').equals(kind).toArray();
  const aSupprimer = lignes
    .filter((ligne) => !ligne.pending && !connus.has(ligne.id))
    .map((ligne) => [kind, ligne.id]);
  if (aSupprimer.length) await db.records.bulkDelete(aSupprimer);
  return aSupprimer.length;
}

// ---------------------------------------------------------------------------
// Dernière réponse connue d'une lecture
// ---------------------------------------------------------------------------

export async function ecrireLecture(entree) {
  // `ts` est l'instant où la requête est partie: une réponse arrivée en retard
  // n'écrase pas une réponse plus récente de la même adresse.
  const ts = entree.ts ?? Date.now();
  const existante = await db.httpCache.get(entree.key);
  if (existante && (existante.ts || 0) > ts) return;
  await db.httpCache.put({ ...entree, ts });
}

export async function lireLecture(cle) {
  return (await db.httpCache.get(cle)) || null;
}

export async function lectureParChemin(chemin) {
  return db.httpCache.where('path').equals(chemin).toArray();
}

// ---------------------------------------------------------------------------
// Correspondance identifiant local -> identifiant serveur
// ---------------------------------------------------------------------------

export async function correspondances() {
  const lignes = await db.idMap.toArray();
  return lignes.reduce((acc, ligne) => {
    acc[ligne.localId] = ligne.serverId;
    return acc;
  }, {});
}

export async function enregistrerCorrespondance(localId, serverId) {
  await db.idMap.put({ localId: String(localId), serverId: String(serverId), ts: Date.now() });
}

// ---------------------------------------------------------------------------
// Forme des réponses par type (tableau nu, enveloppe `data`, avec ou sans `meta`)
// ---------------------------------------------------------------------------

const CLE_FORMES = 'formesDeReponse';
let formes = null;
let enregistrement = null;

async function chargerFormes() {
  if (formes) return formes;
  try {
    const ligne = await db.appState.get(CLE_FORMES);
    formes = ligne?.value && estObjet(ligne.value) ? ligne.value : {};
  } catch (erreur) {
    formes = {};
  }
  return formes;
}

export async function formeDe(kind) {
  return (await chargerFormes())[kind] || null;
}

export async function apprendreForme(kind, partie) {
  const toutes = await chargerFormes();
  const avant = JSON.stringify(toutes[kind] || {});
  toutes[kind] = { ...(toutes[kind] || {}), ...partie };
  if (JSON.stringify(toutes[kind]) === avant) return;
  if (enregistrement) clearTimeout(enregistrement);
  enregistrement = setTimeout(() => {
    enregistrement = null;
    db.appState.put({ key: CLE_FORMES, value: toutes }).catch(() => {});
  }, 400);
}

export const oublierFormes = () => {
  formes = null;
  if (enregistrement) clearTimeout(enregistrement);
  enregistrement = null;
};

// ---------------------------------------------------------------------------
// Lignes calculées sur l'appareil (écritures automatiques du serveur imitées)
// ---------------------------------------------------------------------------
// Elles ne vivent que le temps que le serveur écrive les vraies: une fois
// l'envoi confirmé et la mise à jour reçue, elles sont retirées.

const CLE_DERIVES = 'lignesCalculees';

export async function marquerDerive(kind, id) {
  await db.records.update([kind, String(id)], { derive: true });
  const ligne = await db.appState.get(CLE_DERIVES);
  const liste = Array.isArray(ligne?.value) ? ligne.value : [];
  liste.push([kind, String(id)]);
  await db.appState.put({ key: CLE_DERIVES, value: liste });
}

export async function purgerDerives() {
  const ligne = await db.appState.get(CLE_DERIVES);
  const liste = Array.isArray(ligne?.value) ? ligne.value : [];
  if (liste.length === 0) return 0;
  const enAttente = await db.syncQueue.where('status').equals('pending').toArray();
  const retenues = new Set();
  enAttente
    .filter((element) => element.entity_type === 'http')
    .forEach((element) =>
      (element.data?.locaux || []).forEach((touche) => {
        if (touche.derive) retenues.add(`${touche.kind}|${touche.id}`);
      }),
    );
  const restantes = [];
  const aSupprimer = [];
  liste.forEach(([kind, id]) => {
    if (retenues.has(`${kind}|${id}`)) restantes.push([kind, id]);
    else aSupprimer.push([kind, id]);
  });
  if (aSupprimer.length) await db.records.bulkDelete(aSupprimer);
  await db.appState.put({ key: CLE_DERIVES, value: restantes });
  return aSupprimer.length;
}

// ---------------------------------------------------------------------------
// Fichiers consultés (relus sans réseau)
// ---------------------------------------------------------------------------

const FICHIERS_MAX = 80;
const TAILLE_MAX_FICHIER = 15 * 1024 * 1024;

export async function ecrireFichier(cle, blob) {
  if (!blob || typeof blob.size !== 'number' || blob.size > TAILLE_MAX_FICHIER) return;
  await db.fichiers.put({ cle, blob, type: blob.type || '', ts: Date.now() });
  const total = await db.fichiers.count();
  if (total > FICHIERS_MAX) {
    const anciens = await db.fichiers.orderBy('ts').limit(total - FICHIERS_MAX).primaryKeys();
    await db.fichiers.bulkDelete(anciens);
  }
}

export async function lireFichier(cle) {
  const ligne = await db.fichiers.get(cle);
  return ligne ? ligne.blob : null;
}

export async function renommerFichier(ancienneCle, nouvelleCle) {
  const ligne = await db.fichiers.get(ancienneCle);
  if (!ligne) return;
  await db.fichiers.delete(ancienneCle);
  await db.fichiers.put({ ...ligne, cle: nouvelleCle, ts: Date.now() });
}

// ---------------------------------------------------------------------------
// Fraîcheur: dernière mise à jour reçue du serveur et dernière écriture
// ---------------------------------------------------------------------------

const CLE_DERNIERE_SYNCHRO = 'derniereSynchroLocale';
let derniereSynchro = null;
let derniereEcriture = 0;
let dernierChangement = 0;

/** Une mise à jour (pull) vient d'aboutir: toutes les collections sont à jour à cet instant. */
export async function noterSynchroLocale(instant = Date.now()) {
  derniereSynchro = instant;
  try {
    await db.appState.put({ key: CLE_DERNIERE_SYNCHRO, value: instant });
  } catch (erreur) {
    // la valeur en mémoire suffit pour la session
  }
}

export async function derniereSynchroLocale() {
  if (derniereSynchro !== null) return derniereSynchro;
  try {
    const ligne = await db.appState.get(CLE_DERNIERE_SYNCHRO);
    derniereSynchro = Number(ligne?.value) || 0;
  } catch (erreur) {
    derniereSynchro = 0;
  }
  return derniereSynchro;
}

/** Une mise à jour a apporté des entités nouvelles ou modifiées: les agrégats d'avant sont à revérifier. */
export const noterChangementRecu = (instant = Date.now()) => {
  dernierChangement = Math.max(dernierChangement, instant);
};
export const instantDernierChangement = () => dernierChangement;

/** Une écriture est passée (ici ou rejouée): les instantanés d'avant sont à revérifier. */
export const noterEcriture = (instant = Date.now()) => {
  derniereEcriture = Math.max(derniereEcriture, instant);
};
export const instantDerniereEcriture = () => derniereEcriture;

export const oublierFraicheur = () => {
  derniereSynchro = null;
  derniereEcriture = 0;
  dernierChangement = 0;
};

/** Nombre d'écritures encore à envoyer (file existante). */
export async function nombreEcrituresAEnvoyer() {
  try {
    return await syncQueue.countPending();
  } catch (erreur) {
    return 0;
  }
}
